// User.jsx - User List Component with Firebase
import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db, loginAdmin } from "../../Firebase";
import { ToastContainer, toast } from "react-toastify";
import { useSearchParams, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { CircleLoader } from "react-spinners";
import group42a from "../../assets/group-4@2x.png";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 470,
  bgcolor: "white",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

// 🔑 Hardcoded list of subscribed user IDs
const SUBSCRIBED_USER_IDS = [
  "1L7V2KB59IfGxCoDI2crpwrxLw42",
  "43UVQ3QryjNbjaNBN64WH41j2Bw2",
  "ASFMHUuJo5ZaR4zoncIj49SlbJD2",
  "xjlG7UF04DbtkiWMU11YB019x1t1",
  "ypXWu1MoafdYBzaYTVLgcQvpFr13"
];

const User = () => {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [isSubcribed, setIsSubcribed] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");

  const handleOpen = (uid, isSubscribed, blocked) => {
    setOpen(true);
    setUserId(uid);
    setIsSubcribed(isSubscribed || false);
    setIsBlocked(blocked || false);
  };

  // Fetch all users from Firebase
  const fetchData = async () => {
    try {
      await loginAdmin();
      setIsLoading(true);
      const usersCollection = collection(db, "users");
      const userSnapshot = await getDocs(usersCollection);
      const usersList = userSnapshot.docs.map(doc => ({
        id: doc.id,
        uid: doc.data().uid || doc.id,
        ...doc.data()
      }));
      setProfiles(usersList);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Apply search + subscription filter together
  useEffect(() => {
    const filter = searchParams.get("filter") || "all";

    let result = profiles;

    // Step 1: Apply subscription filter
    if (filter === "subscribed") {
      result = profiles.filter(user => SUBSCRIBED_USER_IDS.includes(user.id));
    } else if (filter === "not_subscribed") {
      result = profiles.filter(user => !SUBSCRIBED_USER_IDS.includes(user.id));
    }

    // Step 2: Apply search filter on top
    if (search) {
      const term = search.toLowerCase();
      result = result.filter(user =>
        (user.name && user.name.toLowerCase().includes(term)) ||
        (user.email && user.email.toLowerCase().includes(term))
      );
    }

    setFilteredUsers(result);
  }, [searchParams, search, profiles]);

  const handleFilterChange = (event) => {
    const value = event.target.value;
    setSearchParams({ filter: value });
  };

  // Subscribe/Unsubscribe user (optional: update Firestore if needed)
  const subscribUserProfile = async (uid, action) => {
    try {
      const userRef = doc(db, "users", uid);
      const isSubscribed = action === "false"; // "false" means subscribe

      await updateDoc(userRef, {
        isSubscribed: isSubscribed
      });

      toast.success(isSubscribed ? "User subscribed successfully" : "User unsubscribed successfully");
      fetchData();
      handleClose();
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast.error("Failed to update subscription");
    }
  };

  // Delete user
  const deleteUserProfile = async (uid) => {
    try {
      await deleteDoc(doc(db, "users", uid));
      toast.success("User deleted successfully");
      fetchData();
      handleClose();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user");
    }
  };

  // Block/Unblock user
  const blockOrUnblock = async (uid, action) => {
    try {
      const userRef = doc(db, "users", uid);
      const blocked = action === "block";

      await updateDoc(userRef, {
        isBlocked: blocked
      });

      toast.success(blocked ? "User blocked successfully" : "User unblocked successfully");
      fetchData();
      handleClose();
    } catch (error) {
      console.error("Error blocking/unblocking user:", error);
      toast.error("Failed to update user status");
    }
  };

  // Navigate to user profile
  const fetchSingleProfile = (uid) => {
    const profile = profiles.find(p => p.id === uid);
    navigate(`/vortexuserProfile/${uid}`, { state: { profile } });
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div className="flex-1 h-screen rounded-xl ml-[19rem] mt-[8rem] text-gray-800 bg-gray-200 flex flex-col items-center justify-start px-8 pb-20 box-border gap-6">
      <ToastContainer />

      <div>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Action
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
              <div className="d-flex justify-end gap-3">
                {isBlocked ? (
                  <button
                    onClick={() => blockOrUnblock(userId, "unblock")}
                    className="px-4 border-0 bg-blue-500 text-white rounded"
                  >
                    Unblock
                  </button>
                ) : (
                  <button
                    onClick={() => blockOrUnblock(userId, "block")}
                    className="px-4 border-0 bg-red-500 text-white rounded"
                  >
                    Block
                  </button>
                )}

                {isSubcribed ? (
                  <button
                    onClick={() => subscribUserProfile(userId, "true")}
                    className="px-4 border-0 bg-yellow-500 text-white rounded"
                  >
                    Unsubscribe
                  </button>
                ) : (
                  <button
                    onClick={() => subscribUserProfile(userId, "false")}
                    className="px-4 border-0 bg-green-500 text-white rounded"
                  >
                    Subscribe
                  </button>
                )}
                <button
                  onClick={() => {
                    deleteUserProfile(userId);
                    handleClose();
                  }}
                  className="px-4 border-0 bg-gray-700 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </Typography>
          </Box>
        </Modal>
      </div>

      <div className="w-full flex flex-row items-center justify-between pt-0 px-0 pb-1 gap-5">
        <div className="flex gap-2 items-center">
          <h1 className="m-0 text-3xl font-bold capitalize text-white">Users</h1>
          <div className="rounded-lg flex items-center py-3 px-4 gap-4 border border-white text-white">
            <span className="text-base">Users:</span>
            <span className="text-base font-bold">{profiles.length}</span>
          </div>
        </div>

        <div>
          <input
            type="text"
            className="py-2 px-4 rounded-lg border-0"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex gap-2 items-center">
          <label htmlFor="filter" className="text-white">Filter Users:</label>
          <select
            id="filter"
            onChange={handleFilterChange}
            value={searchParams.get("filter") || "all"}
            className="py-2 px-3 rounded border-0"
          >
            <option value="all">All</option>
            <option value="subscribed">Subscribed</option>
            <option value="not_subscribed">Not Subscribed</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center w-full h-96">
          <CircleLoader color="#4A90E2" size={50} />
        </div>
      ) : (
        <div className="h-[500px] w-full overflow-y-auto">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((profile) => (
              <div
                key={profile.id}
                className="w-full rounded-lg bg-gray-100 mb-3 text-gray-800 cursor-pointer shadow-md flex items-center justify-between py-4 px-6 gap-5"
              >
                <div onClick={() => fetchSingleProfile(profile.id)} className="flex items-center gap-6">
                  <Stack direction="row" spacing={2}>
                    <Avatar
                      src={profile.imageUrl}
                      alt={profile.name}
                      sx={{ width: 56, height: 56 }}
                    />
                  </Stack>

                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-lg text-white">{profile.name}</span>
                    {SUBSCRIBED_USER_IDS.includes(profile.id) && (
                      <span className="text-yellow-500 text-sm">⭐ Prime Member</span>
                    )}
                  </div>
                </div>

                <div className="flex w-1/2 items-center justify-between">
                  <div className="text-white">{profile.email}</div>
                  {/* <button
                    onClick={() => handleOpen(profile.id, SUBSCRIBED_USER_IDS.includes(profile.id), profile.isBlocked)}
                    className="bg-transparent border-0"
                  >
                    <img
                      className="h-[1.625rem] w-[0.375rem] relative object-contain z-[2]"
                      loading="lazy"
                      alt=""
                      src={group42a}
                    />
                  </button> */}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-white">No users found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default User;